<script setup>
import { useCalendarStore } from "@/views/apps/calendar/useCalendarStore"
import { useCustomerStore } from "@/stores/CustomersStore"
import { useGeneralSearch } from "@/stores/GeneralSearchEngineStore"
import { useModalStore } from "@/stores/ModalStore"
import { useDebounceFn } from '@vueuse/core'
import { useDisplay } from "vuetify"
import { useRoute, useRouter } from 'vue-router'
import { formatPhone } from "@/utils/strings"

// Store instances
const calendarStore = useCalendarStore()
const customerStore = useCustomerStore()
const searchStore = useGeneralSearch()
const modalStore = useModalStore()
const router = useRouter()
const route = useRoute()

// Constants and refs
const THIS_MODAL_NAME = 'searchBoxDialog'
const { mobile } = useDisplay()
const searchQuery = ref('')
const searchInput = ref(null)

// Computed properties
const groupedResults = computed(() => {
  const grouped = {}

  searchStore.generalResults.forEach(item => {
    if (!grouped[item.category]) {
      grouped[item.category] = []
    }
    grouped[item.category].push(item)
  })

  return grouped
})

// Search functionality
const debouncedSearch = useDebounceFn(async () => {
  if (searchQuery.value.trim().length >= 2) {
    await searchStore.getResults(searchQuery.value.trim())
  } else {
    searchStore.generalResults = []
  }
}, 250)

const handleInputChange = (value) => {
  if (value.trim().length >= 2) {
    searchStore.isLoading = true
  } else {
    searchStore.isLoading = false
  }
  debouncedSearch()
}

// Keyboard navigation
const handleKeyDown = (event) => {
  if (event.key === 'ArrowDown') {
    event.preventDefault() // Prevent scrolling

    // Find the first list item and focus it
    const firstItem = document.querySelector('.app-bar-search-dialog .v-list-item')
    if (firstItem) {
      firstItem.focus()
    }
  }
}

// Dialog management
const clearSearchAndCloseDialog = () => {
  searchStore.generalResults = []
  searchQuery.value = ''
  modalStore.closeModal(THIS_MODAL_NAME)
}


const handleResultClick = async (item) => {
  // Save selected item to history
  searchStore.saveSelectedItemToHistory(item)

  // Handle different result types by category ID
  switch (item.catId.toString()) {
  case '2': // Customers
    if (item.identifier) {

      await customerStore.setSelectedCustomer({
        salonerClientId: item.identifier,
      })

      modalStore.closeModal(THIS_MODAL_NAME)
      modalStore.openModal('customerCardDialog')
    }
    break

  case '1': // Services
    // window.location.href = `${import.meta.env.VITE_SALONER10_URL}panel/services/editService/${item.identifier}/`
    window.open(
      `${import.meta.env.VITE_SALONER10_URL}panel/services/editService/${item.identifier}/`,
      '_blank',
      'noopener',
    )
    break

  case '3': // Employees
    window.open(
      `${import.meta.env.VITE_SALONER10_URL}panel/settings/showEmployee/${item.identifier}/`,
      '_blank',
      'noopener',
    )
    break

  case '4': // Visits
    if (route.path === '/calendar') {
      const currentCalendarDate = calendarStore.calendarApi.getDate()
      const visitDate = new Date(item.date)

      if (currentCalendarDate.toDateString() !== visitDate.toDateString()) {
        calendarStore.eventIdToBeOpened = item.identifier
        calendarStore.calendarApi.gotoDate(visitDate)
      } else {
        calendarStore.calendarApi.trigger('eventClick', {
          event: { id: item.identifier, extendedProps: { simulated: true } },
        })
      }
    } else {
      calendarStore.eventIdToBeOpened = item.identifier
      router.push('/calendar?date=' + item.date)
    }
    break
  }
}

// Lifecycle hooks
onMounted(() => {
  if (searchInput.value) {
    searchInput.value.focus()
  }
})
</script>

<template>
  <VDialog
    v-model="modalStore.modals.searchBoxDialog"
    max-width="600"
    :height="!mobile ? '587' : '100%'"
    :fullscreen="mobile"
    class="app-bar-search-dialog"
    scrollable
    @update:model-value="(val) => modalStore.modals.searchBoxDialog = val"
  >
    <VCard
      flat
      :loading="searchStore.isLoading"
    >
      <VCardTitle>
        <div class="d-flex justify-space-between align-center">
          <VTextField
            ref="searchInput"
            v-model="searchQuery"
            density="compact"
            variant="plain"
            :placeholder="$t('search')"
            @update:model-value="handleInputChange"
            @keydown="handleKeyDown"
          >
            <template #prepend>
              <VIcon
                class="mt-1"
                icon="ri-search-line"
              />
            </template>
          </VTextField>
          <VIcon
            icon="ri-close-line"
            @click="clearSearchAndCloseDialog"
          />
        </div>
      </VCardTitle>
      <VDivider />

      <VCardText>
        <!-- Search History -->
        <div
          v-if="searchQuery.trim().length < 2"
          class="pa-2"
        >
          <div class="text-subtitle-1">
            {{ $t('lastResults') }}:
          </div>
          <VList
            v-if="searchStore.searchHistory.length && !searchStore.isLoading"
            class="pa-0"
          >
            <VListItem
              v-for="item in searchStore.searchHistory"
              :key="item.identifier"
              prepend-icon="ri-history-line"
              tabindex="0"
              @click="handleResultClick(item)"
            >
              <VListItemTitle>
                {{ item.label }}
                <span class="text-caption">({{ item.category }})</span>
              </VListItemTitle>
            </VListItem>
          </VList>
          <div
            v-else
            class="text-body-2 pa-2"
          >
            {{ $t('no_results') }}
          </div>
          <div v-if="searchStore.searchHistory.length && !searchStore.isLoading">
            <VBtn
              variant="text"
              class="text-left mt-2"
              color="error"
              @click="searchStore.clearSearchHistory()"
            >
              <template #prepend>
                <VIcon
                  icon="ri-eraser-line"
                  size="x-large"
                />
              </template>
              <span class="text-pre-wrap leading-normal">
                {{ $t('clear_search_history') }}
              </span>
            </VBtn>
          </div>
        </div>

        <!-- Search Results -->
        <div v-else>
          <VList
            density="compact"
            class="pa-0"
          >
            <template
              v-for="(items, category, index) in groupedResults"
              :key="category"
            >
              <VListSubheader class="text-primary">
                {{ category }}
              </VListSubheader>

              <VListItem
                v-for="item in items"
                :key="item.identifier"
                density="compact"
                class="py-1"
                append-icon="ri-corner-down-left-line"
                tabindex="0"
                @click="handleResultClick(item)"
              >
                <VListItemTitle class="">
                  <span
                    v-if="item.label && item.label.trim()"
                    class="mr-2"
                  >
                    {{ item.label }}
                  </span>
                  <span
                    v-if="item.phone && item.phone.trim() && item.catId.toString() !== '3'"
                    class="mr-2 xs-font-size text-disabled"
                  >
                    {{ formatPhone(item.phone) }}
                  </span>
                  <span
                    v-if="item.email && item.email.trim() && item.catId.toString() !== '3'"
                    class="mr-2 xs-font-size text-disabled"
                  >
                    {{ item.email }}
                  </span>
                </VListItemTitle>
              </VListItem>

              <VDivider
                v-if="index < Object.keys(groupedResults).length - 1"
                class="my-2"
              />
            </template>
          </VList>
        </div>

        <!-- No Results Message -->
        <div
          v-if="!searchStore.isLoading && searchStore.generalResults.length === 0 && searchQuery.trim().length >= 2"
          class="pa-2"
        >
          Brak wyników
        </div>
      </VCardText>
    </VCard>
  </VDialog>
</template>
